import { useState, useEffect, useCallback } from 'react';
import { storage } from '../services/storage';
import {
  isCapacitor,
  initGoogleAuth,
  fetchGoogleEvents,
  transformGoogleEvents,
  fetchAppleCalendarEvents,
  transformAppleEvents,
} from '../services/calendarService';

export default function useCalendarSync() {
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const [appleCalConnected] = useState(
    () => storage.getBool('appdhd_apple_connected', false)
  );
  const [googleCalConnected, setGoogleCalConnected] = useState(() =>
    storage.getBool('appdhd_google_connected', false)
  );
  const [googleAccessToken, setGoogleAccessToken] = useState(() =>
    storage.getString('appdhd_google_token', null)
  );
  const [googleEvents, setGoogleEvents] = useState(() =>
    storage.getJson('appdhd_google_events', [])
  );
  const [appleEvents, setAppleEvents] = useState(() =>
    storage.getJson('appdhd_apple_events', [])
  );
  const [isNativeApp] = useState(() => isCapacitor());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [showCalPermissionModal, setShowCalPermissionModal] = useState(false);
  const [pendingCalendar, setPendingCalendar] = useState(null);

  const allEvents = [...googleEvents, ...appleEvents];

  const fetchAndSyncGoogleEvents = useCallback(async (token) => {
    setIsSyncing(true);
    setSyncError(null);
    try {
      const now = new Date();
      const timeMin = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString();
      const timeMax = new Date(now.getFullYear(), now.getMonth() + 6, 0).toISOString();
      const rawEvents = await fetchGoogleEvents(token, timeMin, timeMax);
      const transformed = transformGoogleEvents(rawEvents);
      setGoogleEvents(transformed);
      storage.setJson('appdhd_google_events', transformed);
    } catch (err) {
      console.error('Google Calendar sync error:', err);
      setSyncError('Failed to sync Google Calendar events.');
      if (err.status === 401) {
        setGoogleCalConnected(false);
        setGoogleAccessToken(null);
        setGoogleEvents([]);
        storage.remove('appdhd_google_connected', 'appdhd_google_token', 'appdhd_google_events');
        setSyncError('Google Calendar session expired. Please reconnect.');
      }
    } finally {
      setIsSyncing(false);
    }
  }, []);

  const fetchAndSyncAppleEvents = useCallback(async () => {
    setIsSyncing(true);
    setSyncError(null);
    try {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 6, 0);
      const rawEvents = await fetchAppleCalendarEvents(startDate, endDate);
      const transformed = transformAppleEvents(rawEvents);
      setAppleEvents(transformed);
      storage.setJson('appdhd_apple_events', transformed);
    } catch (err) {
      console.error('Apple Calendar sync error:', err);
      setSyncError('Failed to sync Apple Calendar events.');
    } finally {
      setIsSyncing(false);
    }
  }, []);

  const disconnectGoogle = useCallback(() => {
    setGoogleCalConnected(false);
    setGoogleAccessToken(null);
    setGoogleEvents([]);
    storage.remove('appdhd_google_connected', 'appdhd_google_token', 'appdhd_google_events');
  }, []);

  useEffect(() => {
    if (GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'your-client-id-here.apps.googleusercontent.com') {
      initGoogleAuth(
        GOOGLE_CLIENT_ID,
        (accessToken) => {
          setGoogleAccessToken(accessToken);
          setGoogleCalConnected(true);
          storage.setBool('appdhd_google_connected', true);
          storage.setString('appdhd_google_token', accessToken);
          fetchAndSyncGoogleEvents(accessToken);
        },
        (error) => {
          console.error('Google auth error:', error);
          setSyncError('Failed to connect to Google Calendar.');
        }
      );
    }
  }, [GOOGLE_CLIENT_ID, fetchAndSyncGoogleEvents]);

  useEffect(() => {
    if (googleCalConnected && googleAccessToken) {
      fetchAndSyncGoogleEvents(googleAccessToken);
    }
    if (appleCalConnected && isNativeApp) {
      fetchAndSyncAppleEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    googleCalConnected,
    googleAccessToken,
    googleEvents,
    setGoogleEvents,
    allEvents,
    isSyncing,
    syncError,
    setSyncError,
    showCalPermissionModal,
    setShowCalPermissionModal,
    pendingCalendar,
    setPendingCalendar,
    fetchAndSyncGoogleEvents,
    disconnectGoogle,
    GOOGLE_CLIENT_ID,
  };
}
